import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn } from "lucide-react";
import { LogoMark } from "@/assets";
import { SocialIcons } from "@/components/SocialIcons";
import { useSiteAuth, signInWithGoogle } from "@/components/GoogleAuth";

const LINKS = [
  { label: "The Label", to: "/about" },
  { label: "Leadership", to: "/team" },
  { label: "Services", to: "/services" },
  { label: "Beat Store", to: "/beats" },
  { label: "Merch", to: "/merch" },
  { label: "Book", to: "/booking" },
  { label: "News", to: "/news" },
  { label: "Medusa Room", to: "/medusa" },
  { label: "Medusa Guide", to: "/medusa-guide" },
  { label: "FAQ", to: "/faq" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useSiteAuth();

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const authChip = user === undefined ? null : user ? (
    <button
      onClick={() => go("/account")}
      aria-label="Open your account"
      className="flex items-center gap-2 border border-crystal/40 rounded-full pl-1 pr-3 py-1 hover:border-venom/60 transition-colors"
      data-testid="navbar-account-chip"
    >
      {user.picture ? (
        <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
      ) : (
        <span className="w-7 h-7 rounded-full bg-venom/20 text-venom text-xs font-bold flex items-center justify-center">
          {(user.name || user.email || "M")[0].toUpperCase()}
        </span>
      )}
      <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.15em] text-ice/70 max-w-[90px] truncate">
        {user.name?.split(" ")[0] || "Account"}
      </span>
    </button>
  ) : (
    <button
      onClick={signInWithGoogle}
      className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] border border-white/15 rounded-full px-4 py-2 text-ice/70 hover:border-venom/60 hover:text-venom transition-all"
      data-testid="navbar-signin-button"
    >
      <LogIn size={13} /> Sign In
    </button>
  );

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="main-navbar"
    >
      <div className="glass-panel border-x-0 border-t-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[70px] flex items-center justify-between">
          <button
            onClick={() => go("/")}
            className="flex items-center gap-3 group"
            data-testid="navbar-logo-button"
          >
            <LogoMark className="w-9 h-9 transition-transform duration-500 group-hover:rotate-[8deg]" />
            <span className="font-display font-bold text-white text-sm tracking-wide">
              DGE<span className="text-venom">™</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-5" data-testid="navbar-links">
            {LINKS.map((l) => (
              <button
                key={l.to}
                onClick={() => go(l.to)}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-ice/70 hover:text-venom transition-colors duration-300"
                data-testid={`nav-link-${l.to.slice(1)}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <SocialIcons className="hidden xl:flex" />
            <span className="hidden sm:inline-flex">{authChip}</span>
            <button
              onClick={() => go("/quote")}
              className="hidden sm:inline-flex items-center gap-2 bg-venom text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full hover:scale-[1.04] hover:glow-venom active:scale-95 transition-all duration-300"
              data-testid="navbar-quote-cta"
            >
              Request a Quote
            </button>
            <button
              className="lg:hidden text-ice p-2"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="navbar-mobile-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="lg:hidden glass-panel border-x-0 overflow-hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.to}
                  onClick={() => go(l.to)}
                  className="text-left font-mono text-xs uppercase tracking-[0.2em] text-ice/80 hover:text-venom transition-colors"
                  data-testid={`nav-mobile-link-${l.to.slice(1)}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("/quote")}
                className="mt-2 bg-venom text-obsidian font-semibold text-sm px-5 py-3 rounded-full w-fit"
                data-testid="navbar-mobile-quote-cta"
              >
                Request a Quote
              </button>
              <div>{authChip}</div>
              <SocialIcons className="pt-2 border-t border-white/10" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
